import { supabase } from "@/lib/supabase";

export type EnsurePoCustomerMethod =
  | "already_linked"
  | "matched_existing"
  | "created"
  | "error";

export interface EnsurePoCustomerResult {
  poweroffice_customer_id: number | null;
  method: EnsurePoCustomerMethod;
  /** Hva som faktisk gikk galt, når `method` er "error". */
  error?: string;
}

interface EdgeResponse {
  results?: Array<{
    poweroffice_customer_id: number | null;
    method: string;
    error?: string;
  }>;
}

/**
 * Sikrer at en kunde har en PowerOffice-kobling ved å kalle `ensure-po-customer`
 * edge function. Idempotent og deduper på org-nr server-side.
 *
 * Best-effort: kaster ALDRI. En kunde skal ikke rulles tilbake fordi PO er nede,
 * men `create-invoice` feiler senere uten koblingen — derfor returnerer vi
 * `method: "error"` så caller kan varsle brukeren tydelig.
 *
 * Grunnen følger med i `error`. Edge function-en fanger PO-feilen per kunde og
 * returnerer den i `results[].error`, men den ble tidligere kastet her. Da satt
 * vi igjen med «PowerOffice-kobling feilet» og ingen måte å finne ut hvorfor —
 * feilen når heller ikke Sentry, siden den fanges inne i løkka. Elleve kunder i
 * prod har mistet PO-koblingen siden april uten at noen vet hvorfor.
 */
export async function ensurePoCustomerLink(
  customerId: string,
): Promise<EnsurePoCustomerResult> {
  try {
    const { data, error } = await supabase.functions.invoke<EdgeResponse>(
      "ensure-po-customer",
      { body: { customer_id: customerId } },
    );
    const row = data?.results?.[0];
    if (error || !row || row.method === "error") {
      return {
        poweroffice_customer_id: null,
        method: "error",
        error: row?.error ?? error?.message ?? "Ukjent feil fra ensure-po-customer",
      };
    }
    return {
      poweroffice_customer_id: row.poweroffice_customer_id,
      method: row.method as EnsurePoCustomerMethod,
    };
  } catch (err) {
    return {
      poweroffice_customer_id: null,
      method: "error",
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
